<script lang="ts" setup>
import { computed, ref, watch, nextTick, onMounted } from "vue";
import { useRouter, useRoute } from "vue-router";
import { useQueryClient } from "@tanstack/vue-query";
import { useProjectRuns, useProjectWorkflows, type Paged } from "@/queries/projects";
import { useGetWorkflowRun } from "@/queries/runs";
import { useRunProgress, applyProgress, applyServiceCounts } from "@/composables/useRunProgress";
import { useRunSimulator } from "@/composables/useRunSimulator";
import { useDebugStore } from "@/stores/debug-store";
import type { Project, RunHistory } from "@/types";
import { isFinishedRunStatus } from "@/types";
import HistoryPanel from "@/views/builder/components/HistoryPanel.vue";
import StackedWorkflowView from "@/views/builder/components/StackedWorkflowView.vue";
import RunCanvas from "@/views/runhistory/RunCanvas.vue";
import RunSummaryHeader from "@/views/builder/components/RunSummaryHeader.vue";
import RunWorkflowModal from "@/views/projects/components/tabs/RunWorkflowModal.vue";
import RunStatsModal from "@/views/builder/components/RunStatsModal.vue";
import { useHistoryManagement } from "@/views/builder/composables/useHistoryManagement";
import { useHistoryFilters } from "@/views/builder/composables/useHistoryFilters";
import { useBuilderPrefsStore } from "@/stores/builder-prefs-store";
import { useBuilderNavigation } from "@/views/builder/composables/useBuilderNavigation";

interface Props {
  project: Project | null;
}

const props = defineProps<Props>();

const router = useRouter();
const route = useRoute();

const projectId = computed(() => props.project?.id ?? "");
const filters = useHistoryFilters({ scope: "project", id: projectId });
const { projectRuns, projectRunsTotal, projectRunsLoading, projectRunsFetching } =
  useProjectRuns(projectId, filters.serverFilterParams);
const historyMgmt = useHistoryManagement(projectRuns);
const builderPrefs = useBuilderPrefsStore();

// Canvas vs stack — the SAME persisted pref the builder uses, so the choice follows
// the user across both surfaces. Falls back to stack on small screens, canvas otherwise.
const viewMode = computed<"canvas" | "stack">(
  () => builderPrefs.viewMode ?? (window.innerWidth < 768 ? "stack" : "canvas")
);
function toggleViewMode() {
  builderPrefs.setViewMode(viewMode.value === "canvas" ? "stack" : "canvas");
}

// Workflow-filter options come from the project's full workflow list (not the current run page),
// so the dropdown stays complete regardless of which page / filter is active. Now that runs page
// server-side, deriving options from the loaded rows would only show the current page's workflows.
const projectIdEnabled = computed(() => !!projectId.value);
const { projectWorkflows } = useProjectWorkflows(projectId, projectIdEnabled);

const workflowOptions = computed(() => [
  { value: "all", label: "All Workflows" },
  ...projectWorkflows.value.map((wf) => ({ value: wf.id, label: wf.name })),
]);

// "Open in Builder" routes through the shared navigation resolver so subscribers land on their fork
// (or, for an un-customized base workflow, open read-only history — the builder's edit gate then
// handles confirm-before-forking). Opens in history mode: the user came from a run, so start there.
const builderNav = useBuilderNavigation(projectId);
function openRunInBuilder(run: RunHistory) {
  const wf = projectWorkflows.value.find((w) => w.id === run.workflowId);
  builderNav.openInBuilder(props.project, {
    id: run.workflowId,
    name: wf?.name ?? run.workflowName,
    isCustomized: wf?.isCustomized,
  }, "history");
}

const stackedWorkflowView = ref<InstanceType<typeof StackedWorkflowView> | null>(null);
const runCanvas = ref<InstanceType<typeof RunCanvas> | null>(null);

// Overlay expand/collapse-all for the stacked view — same controls the builder floats
// over its list, driving the stacked view's exposed methods.
function expandAllStacked() {
  stackedWorkflowView.value?.handleExpandAll();
}
function collapseAllStacked() {
  stackedWorkflowView.value?.handleCollapseAll();
}

// --- Route → state sync ----------------------------------------------------

const routeRunId = computed(() => (route.params.runid as string) || "");
const routeWorkflowId = computed(() => (route.params.workflowid as string) || "");
const routeServiceId = computed(() => (route.params.serviceid as string) || "");

// Apply route selection when runs arrive (or when the route changes).
function applyRouteSelection() {
  const targetRunId = routeRunId.value;
  if (!targetRunId) {
    // No deep-link — auto-select first run.
    if (
      !historyMgmt.selectedRun.value &&
      historyMgmt.filteredRunHistory.value.length > 0
    ) {
      historyMgmt.selectRun(historyMgmt.filteredRunHistory.value[0]);
    }
    return;
  }
  const found = historyMgmt.getRunById(targetRunId);
  if (found && historyMgmt.selectedRun.value?.id !== found.id) {
    historyMgmt.selectRun(found);
  }
}

watch(
  [projectRuns, routeRunId],
  () => {
    applyRouteSelection();
  },
  { immediate: true }
);

// When the selected run changes (or the route's serviceid changes), open the
// deep-linked service in whichever view is active — the stacked view expands the
// card; the canvas opens the transaction drill-in.
async function applyServiceExpansion() {
  const serviceId = routeServiceId.value;
  if (!serviceId) return;
  // Wait for the active view to render the new run's nodes before opening.
  await nextTick();
  if (viewMode.value === "canvas") {
    runCanvas.value?.openService(serviceId);
  } else {
    (stackedWorkflowView.value as any).expandNode?.(serviceId);
  }
}

// A source per value, not one getter returning an array: a getter that builds a fresh array is never
// equal to its previous result, so the callback would fire on every change to anything it reads —
// and this one scrolls the expanded card into view, which during a live run is every frame.
watch(
  // viewMode too: toggling canvas ↔ stack while a serviceid is deep-linked should
  // re-open it in the now-active view.
  [() => historyMgmt.selectedRun.value?.id, routeServiceId, viewMode],
  () => {
    applyServiceExpansion();
  }
);

onMounted(() => {
  applyRouteSelection();
  applyServiceExpansion();
});

// --- State → route sync ----------------------------------------------------

function handleSelectRun(run: RunHistory) {
  historyMgmt.selectRun(run);
  router.push({
    name: "project-history-run",
    params: {
      projectid: projectId.value,
      workflowid: run.workflowId,
      runid: run.id,
    },
  });
}

// --- Run actions (project history menu) ----------------------------------
// The builder has its own version of these in BuilderView; the project tab's menu
// mirrors what makes sense outside the editor canvas.

async function copyRunId() {
  const id = historyMgmt.selectedRun.value?.id;
  if (!id) return;
  try {
    await navigator.clipboard.writeText(id);
  } catch {
    // clipboard unavailable; ignore
  }
}

// Re-run opens the same Run Workflow modal the Workflows tab uses — inputs editable and prefilled
// from the original run, plus the safe-by-default readiness confirm gate. The modal starts a fresh
// run and navigates to it, so there's no need to touch canvas state here.
// Debug-only run-animation simulator — feeds fake progress frames through the live-update path so the
// canvas/list edges animate without a real backend run. Gated on session debug mode (?debug=1).
const debug = useDebugStore();
const simulator = useRunSimulator(historyMgmt.selectedRun);

const statsOpen = ref(false);
const rerunModalOpen = ref(false);
const rerunWorkflowId = ref<string | null>(null);
const rerunWorkflowName = ref<string | undefined>(undefined);
const rerunInputs = ref<Record<string, unknown>>({});
const getWorkflowRun = useGetWorkflowRun();

// The inputs a run was started with are read on demand, not carried on the list row — they live on the
// run's context, so a page of rows would be a document read each.
async function rerunSelectedRun() {
  const run = historyMgmt.selectedRun.value;
  if (!run) return;
  rerunWorkflowId.value = run.workflowId;
  rerunWorkflowName.value = run.workflowName;
  rerunInputs.value = {};
  rerunModalOpen.value = true;

  const detail = await getWorkflowRun(run.id);
  // Only fill the run the user actually asked for — they can pick another while this is in flight.
  if (rerunModalOpen.value && rerunWorkflowId.value === run.workflowId)
    rerunInputs.value = detail.inputs ?? {};
}

// --- Live progress ---------------------------------------------------------

// A run the engine has not finished with is one worth following. Stated as "not finished" rather than
// as a list of live statuses: a run awaiting a slot reads Queued and one in flight reads Pending, and
// naming the live ones would leave anything the engine adds later unwatched.
const queryClient = useQueryClient();

const runProgress = useRunProgress({
  onProgress: (update) => {
    if (historyMgmt.selectedRun.value?.id === update.runId) {
      const applied = applyProgress(historyMgmt.selectedRun.value, update);
      if (applied) historyMgmt.selectedRun.value = applied;
    }
    applyToRunList(update, applyProgress);
  },
  onServiceCounts: (update) => {
    if (historyMgmt.selectedRun.value?.id === update.runId) {
      const applied = applyServiceCounts(historyMgmt.selectedRun.value, update);
      if (applied) historyMgmt.selectedRun.value = applied;
    }
    applyToRunList(update, applyServiceCounts);
  },
  onFinished: async (finished) => {
    // Frames are lossy, and duration, completedAt and the final counts live on the run document —
    // so the last word is a read, not the last frame.
    void queryClient.invalidateQueries({ queryKey: ["project-runs", projectId.value] });
    const fetched = await getWorkflowRun(finished.runId);
    if (fetched && historyMgmt.selectedRun.value?.id === fetched.id)
      historyMgmt.selectedRun.value = fetched;
  },
});

// Show a "live updates are down" banner only when it actually matters: the selected run is still
// going, so a broken feed leaves it looking frozen. A finished run needs no live feed.
const liveConnectionLost = computed(() => {
  const run = historyMgmt.selectedRun.value;
  return (
    !!runProgress.connectionError.value &&
    !!run &&
    !isFinishedRunStatus(run.status)
  );
});

// The row and the canvas are two views of the same run, so the frame lands on both — otherwise a run
// the user is not looking at sits on its starting status until something refetches the page. Every
// cached page for this project is patched: the key carries the filter string, which lives inside
// useProjectRuns and isn't ours to reconstruct.
function applyToRunList<TUpdate extends { runId: string }>(
  update: TUpdate,
  apply: (previous: RunHistory | null, update: TUpdate) => RunHistory | null
) {
  queryClient.setQueriesData<Paged<RunHistory>>(
    { queryKey: ["project-runs", projectId.value] },
    (page) => {
      if (!page) return page;
      const index = page.items.findIndex((run) => run.id === update.runId);
      if (index < 0) return page;
      const applied = apply(page.items[index], update);
      if (!applied) return page;
      const items = [...page.items];
      items[index] = applied;
      return { ...page, items };
    }
  );
}

watch(
  [projectRuns, historyMgmt.selectedRun],
  ([runs, selected]) => {
    const visible = selected ? [...runs, selected] : runs;
    void runProgress.watch(
      [...new Set(visible.filter((run) => !isFinishedRunStatus(run.status)).map((run) => run.id))]
    );
  },
  { immediate: true }
);

function exportRunData() {
  // TODO: matches the builder's stub — wire up once the run data export endpoint exists.
  const run = historyMgmt.selectedRun.value;
  if (!run) return;
}

function handleCardExpansionChanged(nodeId: string, expanded: boolean) {
  const run = historyMgmt.selectedRun.value;
  if (!run) return;
  if (expanded) {
    router.replace({
      name: "project-history-service",
      params: {
        projectid: projectId.value,
        workflowid: run.workflowId,
        runid: run.id,
        serviceid: nodeId,
      },
    });
  } else if (routeServiceId.value === nodeId) {
    // Collapsed the currently-deep-linked card — drop the serviceid segment.
    router.replace({
      name: "project-history-run",
      params: {
        projectid: projectId.value,
        workflowid: run.workflowId,
        runid: run.id,
      },
    });
  }
}

// --- Empty states ----------------------------------------------------------

const hasFilteredRuns = computed(
  () => historyMgmt.filteredRunHistory.value.length > 0
);

// If the page is *entered* via a deep-link that carries a workflow id (and no
// filter is set yet), default the workflow filter to it so the list highlights
// the right rows. This must only happen on initial load: selecting a run also
// writes `workflowid` into the route, and we don't want that to hijack the
// filter dropdown on every click.
onMounted(() => {
  const id = routeWorkflowId.value;
  if (id && filters.historyWorkflowFilter.value === "all") {
    filters.updateWorkflowFilter(id);
  }
});
</script>

<template>
  <div class="tab-pane fade show active">
    <!-- Loading State -->
    <div v-if="projectRunsLoading" class="text-center py-5">
      <div class="spinner-border text-primary" role="status">
        <span class="visually-hidden">Loading...</span>
      </div>
      <p class="text-muted mt-3">Loading run history...</p>
    </div>

    <template v-else>
      <!-- Always render the layout — filter state determines what comes back,
           so even an empty result set should still show the filter panel so
           the user can adjust the time range / statuses to find their runs. -->
      <div class="history-tab-layout">
        <HistoryPanel
          scope="project"
          :loading="false"
          :filtered-run-history="historyMgmt.filteredRunHistory.value"
          :selected-run="historyMgmt.selectedRun.value"
          :search-query="filters.historySearchQuery.value"
          :status-filter="filters.historyStatusFilter.value"
          :status-filters="filters.historyStatusFilters.value"
          :time-shortcut="filters.historyTimeShortcut.value"
          :date-from="filters.historyDateFrom.value"
          :date-to="filters.historyDateTo.value"
          :workflow-filter="filters.historyWorkflowFilter.value"
          :workflow-options="workflowOptions"
          :sort-by="filters.historySortBy.value"
          :page-length="filters.historyPageSize.value"
          :total-count="projectRunsTotal"
          :page="filters.historyPage.value"
          :fetching="projectRunsFetching"
          :advanced-open="builderPrefs.historyFiltersAdvancedOpen"
          :advanced-filters-dirty="filters.advancedFiltersDirty.value"
          :grouped="builderPrefs.groupChildRuns"
          @update-search-query="filters.updateSearchQuery"
          @update-status-filter="filters.updateStatusFilter"
          @toggle-status="filters.toggleStatusFilter"
          @update-time-shortcut="filters.updateTimeShortcut"
          @update-date-range="(from, to) => filters.updateDateRange(from, to)"
          @update-workflow-filter="filters.updateWorkflowFilter"
          @update-sort-by="filters.updateSortBy"
          @update-page-size="filters.updatePageSize"
          @update-page="filters.updatePage"
          @update-advanced-open="builderPrefs.setHistoryFiltersAdvancedOpen"
          @update-grouped="builderPrefs.setGroupChildRuns"
          @reset="filters.resetHistoryFilters"
          @select-run="handleSelectRun"
        />

        <div class="history-tab-detail flex-fill d-flex flex-column">
          <template v-if="historyMgmt.selectedRun.value">
            <RunSummaryHeader
              :run="historyMgmt.selectedRun.value"
              :project-id="projectId"
              hide-open-in-builder
            >
              <template #actions>
                <div class="dropdown">
                  <button
                    class="bb-btn-secondary-cta btn-sm"
                    type="button"
                    data-bs-toggle="dropdown"
                    aria-expanded="false"
                    title="More options"
                  >
                    <i class="bi bi-three-dots"></i>
                  </button>
                  <ul class="dropdown-menu dropdown-menu-end">
                    <li>
                      <a
                        v-if="historyMgmt.selectedRun.value"
                        class="dropdown-item"
                        href="#"
                        @click.prevent="openRunInBuilder(historyMgmt.selectedRun.value)"
                      >
                        <i class="bi bi-wrench me-2"></i>Open in Builder
                      </a>
                    </li>
                    <li>
                      <a class="dropdown-item" href="#" @click.prevent="rerunSelectedRun">
                        <i class="bi bi-arrow-clockwise me-2"></i>Re-run
                      </a>
                    </li>
                    <li>
                      <a class="dropdown-item" href="#" @click.prevent="exportRunData">
                        <i class="bi bi-download me-2"></i>Export Run Data
                      </a>
                    </li>
                    <li><hr class="dropdown-divider" /></li>
                    <li>
                      <a class="dropdown-item" href="#" @click.prevent="statsOpen = true">
                        <i class="bi bi-speedometer2 me-2"></i>Stats for nerds
                      </a>
                    </li>
                    <li>
                      <a class="dropdown-item" href="#" @click.prevent="copyRunId">
                        <i class="bi bi-clipboard me-2"></i>Copy Run ID
                      </a>
                    </li>
                    <template v-if="debug.debugToolsVisible">
                      <li><hr class="dropdown-divider" /></li>
                      <li>
                        <h6 class="dropdown-header text-muted small text-uppercase">Debug</h6>
                      </li>
                      <li>
                        <a
                          class="dropdown-item small text-muted"
                          :class="{ disabled: simulator.running.value }"
                          href="#"
                          @click.prevent="simulator.start()"
                        >
                          <i class="bi bi-play-circle me-2"></i>Simulate run
                        </a>
                      </li>
                    </template>
                  </ul>
                </div>
              </template>
            </RunSummaryHeader>
            <!-- Live feed down while the run is still going — say so, else the canvas looks frozen-but-done. -->
            <div
              v-if="liveConnectionLost"
              class="alert alert-warning d-flex align-items-center gap-2 mb-0 rounded-0 py-2"
              role="alert"
            >
              <i class="bi bi-wifi-off"></i>
              <span>Live updates disconnected — this run may have progressed since. Refresh to see the latest.</span>
            </div>
            <div class="flex-fill position-relative overflow-hidden">
              <!-- Canvas view — the same read-only run canvas the builder shows. The
                   view-mode toggle rides at the top of the canvas's own floating
                   control cluster, so it looks identical to the builder. -->
              <RunCanvas
                v-if="viewMode === 'canvas'"
                ref="runCanvas"
                :nodes="historyMgmt.historyNodes.value"
                :edges="historyMgmt.historyEdges.value"
                :run-id="historyMgmt.selectedRun.value.id"
              >
                <template #controls-top>
                  <button
                    type="button"
                    class="btn bb-btn-vueflow-ctrl d-flex align-items-center justify-content-center"
                    title="Switch to List View"
                    @click="toggleViewMode"
                  >
                    <i class="bi bi-list-ul"></i>
                  </button>
                </template>
              </RunCanvas>
              <!-- Stack view — existing card list. A matching floating control
                   cluster (toggle + expand/collapse) sits top-right over the list,
                   the same spot and style as the builder. -->
              <template v-else>
                <div class="run-canvas-controls position-absolute d-flex flex-column gap-1">
                  <button
                    type="button"
                    class="btn bb-btn-vueflow-ctrl d-flex align-items-center justify-content-center"
                    title="Switch to Canvas View"
                    @click="toggleViewMode"
                  >
                    <i class="bi bi-diagram-3"></i>
                  </button>
                  <span class="border-bottom my-1"></span>
                  <button
                    type="button"
                    class="btn bb-btn-vueflow-ctrl d-flex align-items-center justify-content-center"
                    title="Expand All Services"
                    @click="expandAllStacked"
                  >
                    <i class="bi bi-chevron-bar-down"></i>
                  </button>
                  <button
                    type="button"
                    class="btn bb-btn-vueflow-ctrl d-flex align-items-center justify-content-center"
                    title="Collapse All Services"
                    @click="collapseAllStacked"
                  >
                    <i class="bi bi-chevron-bar-up"></i>
                  </button>
                  <button
                    type="button"
                    class="btn bb-btn-vueflow-ctrl d-flex align-items-center justify-content-center"
                    :class="{ active: builderPrefs.flattenStack }"
                    :title="builderPrefs.flattenStack ? 'Restore indentation' : 'Flatten to full width'"
                    @click="builderPrefs.setFlattenStack(!builderPrefs.flattenStack)"
                  >
                    <i :class="builderPrefs.flattenStack ? 'bi bi-text-indent-right' : 'bi bi-text-indent-left'"></i>
                  </button>
                </div>
                <StackedWorkflowView
                  ref="stackedWorkflowView"
                  :nodes="historyMgmt.historyNodes.value"
                  :edges="historyMgmt.historyEdges.value"
                  canvas-mode="history"
                  :has-overlay-controls="true"
                  :flat="builderPrefs.flattenStack"
                  :run-id="historyMgmt.selectedRun.value.id"
                  :run-workflow-id="historyMgmt.selectedRun.value.workflowId"
                  :effective-status-by-service="historyMgmt.effectiveStatusByService.value"
                  :run-coloring-active="historyMgmt.runColoringActive.value"
                  @card-expansion-changed="handleCardExpansionChanged"
                />
              </template>
            </div>
          </template>
          <div
            v-else-if="hasFilteredRuns"
            class="d-flex align-items-center justify-content-center text-muted h-100"
          >
            <div class="text-center py-5">
              <i class="bi bi-arrow-left display-4"></i>
              <p class="mt-3">Select a run to view its services</p>
            </div>
          </div>
          <div
            v-else
            class="d-flex align-items-center justify-content-center text-muted h-100"
          >
            <div class="text-center py-5">
              <i class="bi bi-funnel display-4"></i>
              <h3 class="mt-3 text-muted">No matching runs</h3>
              <p class="text-muted">
                Try widening the time range or clearing other filters.
              </p>
              <button
                type="button"
                class="btn btn-sm btn-outline-secondary"
                @click="filters.resetHistoryFilters"
              >
                <i class="bi bi-arrow-counterclockwise me-1"></i>Reset filters
              </button>
            </div>
          </div>
        </div>
      </div>
    </template>

    <!-- Re-run gate: editable inputs (prefilled from the original run) + readiness confirm. -->
    <RunWorkflowModal
      v-model:show="rerunModalOpen"
      :project-id="projectId"
      :workflow-id="rerunWorkflowId"
      :workflow-name="rerunWorkflowName"
      :initial-inputs="rerunInputs"
    />

    <RunStatsModal v-model:show="statsOpen" :run="historyMgmt.selectedRun.value" />
  </div>
</template>

<style scoped lang="scss">
.history-tab-layout {
  display: flex;
  flex-direction: row;
  align-items: stretch;
  min-height: 600px;
  border: 1px solid var(--bs-border-color);
  overflow: hidden;
  // Anchor the absolutely-positioned collapse toggle (and the mobile panel
  // overlay) to this container, matching the builder's canvas wrapper.
  position: relative;
}

.history-tab-detail {
  min-width: 0;
  // Match the builder's stacked view — grey surface with the logo watermark
  // anchored bottom-right. The service cards are transparent, so this shows
  // through behind them. Scoped to the pane (not the page body) so the mark
  // stays inside the detail area.
  background: var(--bs-tertiary-bg) url(/images/watermark.png) bottom right no-repeat;
}

// Mobile — pull the floating controls in to match the tightened list spacing (top/right
// mirror the service stack's mobile padding-top and the header panel's edge gap).
@media (max-width: 768px) {
  .history-tab-detail .run-canvas-controls {
    top: 0.375rem;
    right: 0.5rem;
  }
}

</style>
